import { Card, CardContent } from '../../../components/ui/card';
import { Users, CheckCircle2, Clock, AlertTriangle, type LucideIcon } from 'lucide-react';
import type { UploadSummary } from '../../../lib/api/barratt';
import { formatDay } from '../uploadCycle';

interface UploadKpiTilesProps {
  summary: UploadSummary | null;
  loading: boolean;
}

interface Tile {
  label: string;
  value: string;
  sub?: string;
  icon: LucideIcon;
  iconClass: string;
  cardClass?: string;
}

/**
 * Suppliers due / Uploaded / Pending / Overdue for the selected period.
 *
 * Every tile counts suppliers. They used to mix units — "Suppliers due 219"
 * beside "Overdue 436", which counted files — so the page appeared to say that
 * twice as many suppliers were late as were asked. The file counts still come
 * back from the endpoint and are named in the tile's own sub-line.
 */
export default function UploadKpiTiles({ summary, loading }: UploadKpiTilesProps) {
  const files = (n: number) => `${n.toLocaleString()} file${n === 1 ? '' : 's'}`;
  const tiles: Tile[] = [
    {
      label: 'Suppliers due',
      value: summary ? summary.total_suppliers.toLocaleString() : '—',
      sub: summary ? `${files(summary.total_obligations)} · due ${formatDay(summary.due_date)}` : undefined,
      icon: Users,
      iconClass: 'text-blue-500',
    },
    {
      label: 'All files in',
      value: summary ? summary.suppliers_uploaded.toLocaleString() : '—',
      sub: summary ? `${Number(summary.suppliers_uploaded_pct ?? 0).toFixed(0)}% of suppliers` : undefined,
      icon: CheckCircle2,
      iconClass: 'text-green-500',
    },
    {
      label: 'Pending',
      value: summary ? summary.suppliers_pending.toLocaleString() : '—',
      sub: summary ? `Not yet due · ${files(summary.pending)}` : undefined,
      icon: Clock,
      iconClass: 'text-yellow-500',
    },
    {
      label: 'Overdue',
      value: summary ? summary.suppliers_overdue.toLocaleString() : '—',
      sub: summary ? `Past the due date · ${files(summary.overdue)}` : undefined,
      icon: AlertTriangle,
      iconClass: 'text-red-500',
      cardClass: summary && summary.suppliers_overdue > 0 ? 'border-red-200 bg-red-50' : undefined,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-4" data-testid="upload-kpi-tiles">
      {tiles.map((tile) => {
        const Icon = tile.icon;
        return (
          <Card key={tile.label} className={tile.cardClass}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{tile.label}</p>
                  {loading ? (
                    <div className="mt-1 h-8 w-16 animate-pulse rounded bg-muted" />
                  ) : (
                    <p className="text-2xl font-bold">{tile.value}</p>
                  )}
                  {!loading && tile.sub && (
                    <p className="text-xs text-muted-foreground">{tile.sub}</p>
                  )}
                </div>
                <Icon className={`h-8 w-8 ${tile.iconClass}`} />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
